import { tKey, type I18nKey } from "@/core/i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import { ProjectTypeEnum } from "@repo/schema";
import { useTranslation } from "react-i18next";
import { TRAINING_TYPE_LABELS } from "../../constants/labels";
import { SettingsCard } from "../SettingsCard";

// Classification is deliberately not offered here. ml-yolo supports it, but the
// metrics surface (headlineMetric.ts, the Overview charts) is built around
// mAP50 and would need accuracy_top1 handling first — the enum value stays for
// historical models.
const SELECTABLE_TRAINING_TYPES = [
  ProjectTypeEnum.DETECTION,
  ProjectTypeEnum.SEGMENTATION,
];

type DetectionAnnotationPreset = "detection" | "segmentation" | "both";

const PRESET_LABELS = {
  detection: tKey("model:typeSettings.preset.detection"),
  segmentation: tKey("model:typeSettings.preset.segmentation"),
  both: tKey("model:typeSettings.preset.both"),
} satisfies Record<DetectionAnnotationPreset, I18nKey<"model">>;

const ANNOTATIONS_LABELS = {
  [ProjectTypeEnum.CLASSIFICATION]: tKey("model:typeSettings.annotations.CLASSIFICATION"),
  [ProjectTypeEnum.DETECTION]: tKey("model:typeSettings.annotations.DETECTION"),
  [ProjectTypeEnum.SEGMENTATION]: tKey("model:typeSettings.annotations.SEGMENTATION"),
} satisfies Record<ProjectTypeEnum, I18nKey<"model">>;

const presetToAnnotations = (
  preset: DetectionAnnotationPreset,
): ProjectTypeEnum[] => {
  if (preset === "detection") return [ProjectTypeEnum.DETECTION];
  if (preset === "segmentation") return [ProjectTypeEnum.SEGMENTATION];
  return [ProjectTypeEnum.DETECTION, ProjectTypeEnum.SEGMENTATION];
};

const annotationsToPreset = (
  annotations: ProjectTypeEnum[],
): DetectionAnnotationPreset => {
  if (
    annotations.includes(ProjectTypeEnum.DETECTION) &&
    annotations.includes(ProjectTypeEnum.SEGMENTATION)
  )
    return "both";
  if (annotations.includes(ProjectTypeEnum.SEGMENTATION)) return "segmentation";
  return "detection";
};

export interface ModelTypeSettingsProps {
  trainingType: ProjectTypeEnum;
  annotationsUsed: ProjectTypeEnum[];
  onTrainingTypeChange: (type: ProjectTypeEnum) => void;
  onAnnotationsUsedChange: (annotations: ProjectTypeEnum[]) => void;
}

export const ModelTypeSettings = ({
  trainingType,
  annotationsUsed,
  onTrainingTypeChange,
  onAnnotationsUsedChange,
}: ModelTypeSettingsProps) => {
  const { t } = useTranslation(["model", "common"]);
  const handleTrainingTypeChange = (type: ProjectTypeEnum) => {
    onTrainingTypeChange(type);
    if (type === ProjectTypeEnum.DETECTION) {
      onAnnotationsUsedChange([
        ProjectTypeEnum.DETECTION,
        ProjectTypeEnum.SEGMENTATION,
      ]);
    } else {
      onAnnotationsUsedChange([type]);
    }
  };

  return (
    <SettingsCard title={t("typeSettings.title")} state="complete" stepNumber={1}>
      <div className="flex w-full flex-1 flex-row items-start gap-9">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-xs font-normal text-black/90">
            {t("fields.trainingType")}
          </span>
          <Select
            value={trainingType}
            onValueChange={(v) =>
              handleTrainingTypeChange(v as ProjectTypeEnum)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("typeSettings.selectType")}>
                {(value) =>
                  value
                    ? t(TRAINING_TYPE_LABELS[value as ProjectTypeEnum])
                    : t("typeSettings.selectType")
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {SELECTABLE_TRAINING_TYPES.map((v) => (
                <SelectItem key={v} value={v}>
                  {t(TRAINING_TYPE_LABELS[v])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {trainingType === ProjectTypeEnum.DETECTION ? (
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-xs font-normal text-black/90">
              {t("fields.annotationsUsed")}
            </span>
            <Select
              value={annotationsToPreset(annotationsUsed)}
              onValueChange={(v) =>
                onAnnotationsUsedChange(
                  presetToAnnotations(v as DetectionAnnotationPreset),
                )
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("typeSettings.selectAnnotations")}>
                  {(value) =>
                    value
                      ? t(PRESET_LABELS[value as DetectionAnnotationPreset])
                      : t("typeSettings.selectAnnotations")
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="detection">
                  {t(PRESET_LABELS.detection)}
                </SelectItem>
                <SelectItem value="segmentation">
                  {t(PRESET_LABELS.segmentation)}
                </SelectItem>
                <SelectItem value="both">{t(PRESET_LABELS.both)}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-xs font-normal text-black/90">
              {t("fields.annotationsUsed")}
            </span>
            <span className="text-sm">{t(ANNOTATIONS_LABELS[trainingType])}</span>
          </div>
        )}
      </div>
    </SettingsCard>
  );
};
